import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';

import { cn } from '../../../lib/utils.js';

export interface AppShellSidebarProps extends HTMLAttributes<HTMLElement> {
  expanded?: boolean;
  children: ReactNode;
}

export function AppShellSidebar({
  expanded = false,
  className,
  children,
  ...props
}: AppShellSidebarProps) {
  return (
    <aside
      className={cn('shell-sidebar', className)}
      data-expanded={expanded ? 'true' : 'false'}
      {...props}
    >
      {children}
    </aside>
  );
}

export type AppShellSidebarBrandProps = HTMLAttributes<HTMLDivElement>;

export function AppShellSidebarBrand({ className, children, ...props }: AppShellSidebarBrandProps) {
  return (
    <div className={cn('shell-sidebar__brand', className)} {...props}>
      {children}
    </div>
  );
}

export type AppShellSidebarBrandLabelProps = HTMLAttributes<HTMLSpanElement>;

export function AppShellSidebarBrandLabel({
  className,
  children,
  ...props
}: AppShellSidebarBrandLabelProps) {
  return (
    <span className={cn('shell-sidebar__brand-label', className)} {...props}>
      {children}
    </span>
  );
}

export type AppShellSidebarNavProps = HTMLAttributes<HTMLElement>;

export function AppShellSidebarNav({ className, children, ...props }: AppShellSidebarNavProps) {
  return (
    <nav className={cn('shell-sidebar__nav', className)} {...props}>
      {children}
    </nav>
  );
}

export interface AppShellSidebarItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  icon?: ReactNode;
  label?: ReactNode;
  trailing?: ReactNode;
}

export function AppShellSidebarItem({
  active = false,
  icon,
  label,
  trailing,
  className,
  children,
  type = 'button',
  ...props
}: AppShellSidebarItemProps) {
  return (
    <button
      type={type}
      className={cn('shell-sidebar__item', className)}
      data-active={active ? 'true' : 'false'}
      {...props}
    >
      {icon ? <span className="shell-sidebar__item-icon">{icon}</span> : null}
      {label !== undefined ? <span className="shell-sidebar__item-label">{label}</span> : children}
      {trailing}
    </button>
  );
}

export type AppShellSidebarGroupProps = HTMLAttributes<HTMLDivElement> & {
  open?: boolean;
};

export function AppShellSidebarGroup({
  open = false,
  className,
  children,
  ...props
}: AppShellSidebarGroupProps) {
  return (
    <div
      className={cn('shell-sidebar__group', className)}
      data-open={open ? 'true' : 'false'}
      {...props}
    >
      {children}
    </div>
  );
}

export type AppShellSidebarChildrenProps = HTMLAttributes<HTMLUListElement>;

export function AppShellSidebarChildren({
  className,
  children,
  ...props
}: AppShellSidebarChildrenProps) {
  return (
    <ul className={cn('shell-sidebar__children', className)} {...props}>
      {children}
    </ul>
  );
}

export interface AppShellSidebarChildProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  icon?: ReactNode;
  label?: ReactNode;
}

export function AppShellSidebarChild({
  active = false,
  icon,
  label,
  className,
  type = 'button',
  ...props
}: AppShellSidebarChildProps) {
  return (
    <li>
      <button
        type={type}
        className={cn('shell-sidebar__child', className)}
        data-active={active ? 'true' : 'false'}
        {...props}
      >
        {icon ? (
          <span className="shell-sidebar__child-icon" aria-hidden>
            {icon}
          </span>
        ) : null}
        {label !== undefined ? <span className="shell-sidebar__child-label">{label}</span> : null}
      </button>
    </li>
  );
}

export type AppShellSidebarFooterProps = HTMLAttributes<HTMLDivElement>;

export function AppShellSidebarFooter({
  className,
  children,
  ...props
}: AppShellSidebarFooterProps) {
  return (
    <div className={cn('shell-sidebar__footer', className)} {...props}>
      {children}
    </div>
  );
}

export type AppShellSidebarGroupChevronProps = HTMLAttributes<HTMLSpanElement>;

export function AppShellSidebarGroupChevron({
  className,
  children,
  ...props
}: AppShellSidebarGroupChevronProps) {
  return (
    <span className={cn('shell-sidebar__group-chevron', className)} aria-hidden {...props}>
      {children}
    </span>
  );
}
